/**
 * WelcomeTutorial - w2_05, fuller pages in w2_06, w2_07 copy. The walkthrough, in the
 * Worthly 2.0 look.
 *
 * A start page with the whole tour as a list, one page per tab (Home,
 * Budget, Wealth, Calendar, Wishlist), then "Keeping your money safe".
 * Each tab page: a card like the real one on that tab, which tab it is,
 * four things to know and a "Try this first" tip.
 * Shown after setup and from Settings. A tab's help button opens just that
 * tab's page (startSlide 1-5) with "Got it". `aboveTabs` lifts the buttons
 * above the tab bar when it's opened from inside the app.
 */
import { useState } from "react";
import { W2, W2Page, W2Bubble, W2Dots, w2Title, w2Sub, w2Btn, w2BackBtn } from "./w2ui";

// Slide numbers, used by the help buttons on each tab (App.js).
export const TUTORIAL_SLIDES = { start: 0, home: 1, budget: 2, wealth: 3, networth: 3, calendar: 4, wishlist: 5, safety: 6 };

const Node = ({ e, label, sub, done, on, bg, pill }) => (
  <div style={{ position: 'relative', zIndex: 1, flex: 1, textAlign: 'center', minWidth: 0 }}>
    <div
      className="flex items-center justify-center"
      style={{
        width: 34, height: 34, margin: '0 auto', borderRadius: 999, fontSize: done ? 12 : 14,
        background: done ? W2.lilDot : bg || '#FFFFFF', color: done ? '#FFFFFF' : W2.ink,
        border: bg ? 0 : `2.5px solid ${done ? W2.lilDot : on ? '#B9A6EE' : W2.lil}`,
        boxShadow: on ? `0 0 0 3px ${W2.lil}` : 'none',
      }}
    >
      {done ? '✓' : e}
    </div>
    <div style={{ fontSize: 10, color: W2.soft, marginTop: 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 9.5, fontWeight: 600, color: sub === 'Passed' ? W2.soft : W2.lilT, marginTop: 1 }}>{sub}</div>}
    {pill && <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 999, marginTop: 3, background: pill.bg, color: pill.ink }}>{pill.text}</span>}
  </div>
);

const MiniPath = ({ dashed, children }) => (
  <div className="flex items-start justify-between" style={{ position: 'relative', marginTop: 12 }}>
    <div style={{ position: 'absolute', left: 18, right: 18, top: 16, height: dashed ? 0 : 2.5, background: dashed ? 'none' : W2.lil, borderTop: dashed ? '2.5px dashed #D9CFF4' : 0 }} />
    {children}
  </div>
);

const Bar = ({ pct, colour, style }) => (
  <div style={{ height: 6, borderRadius: 9, background: '#F0EDF5', overflow: 'hidden', ...style }}>
    <i style={{ display: 'block', height: '100%', width: pct + '%', background: colour, borderRadius: 9 }} />
  </div>
);

// The preview: a white card like the real one, straight on the page.
const Preview = ({ children }) => (
  <div style={{ background: '#FFFFFF', borderRadius: 24, padding: 16, boxShadow: '0 6px 20px rgba(80,60,130,0.08)', marginTop: 16 }} aria-hidden="true">{children}</div>
);

const cTitle = { fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em', color: W2.ink };
const cSub = { fontSize: 11.5, color: W2.soft, marginTop: 2 };

const Feat = ({ e, bg, title, text }) => (
  <div style={{ background: '#FFFFFF', borderRadius: 20, padding: 12, minWidth: 0 }}>
    <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 12, background: bg, fontSize: 16 }}>{e}</span>
    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: W2.ink, marginTop: 9 }}>{title}</span>
    <span style={{ display: 'block', fontSize: 11.5, color: W2.soft, lineHeight: 1.4, marginTop: 2 }}>{text}</span>
  </div>
);

const WideFeat = ({ e, bg, title, text }) => (
  <div className="flex items-center" style={{ gap: 12, background: '#FFFFFF', borderRadius: 20, padding: 12, marginTop: 8 }}>
    <span className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 12, background: bg, fontSize: 16 }}>{e}</span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: W2.ink }}>{title}</span>
      <span style={{ display: 'block', fontSize: 12, color: W2.soft, lineHeight: 1.4, marginTop: 2 }}>{text}</span>
    </span>
  </div>
);

const Tip = ({ label = 'Try this first:', children }) => (
  <div style={{ background: W2.butter, color: '#5E5132', borderRadius: 18, padding: '12px 14px', fontSize: 13, lineHeight: 1.5, marginTop: 10 }}>
    <b style={{ fontWeight: 600, color: W2.butterT }}>{label}</b> {children}
  </div>
);

const monthName = () => new Date().toLocaleDateString('en-US', { month: 'long' });

const TABS = [
  {
    key: 'home', e: '\u{1F3E0}', name: 'Home', line: 'Your money at a glance', bg: W2.lil, ink: W2.lilT, where: 'First tab',
    sub: 'Your money at a glance.', // w2_08
    preview: () => (
      <>
        <div style={cTitle}>Your money timeline {'✨'}</div>
        <div style={cSub}>The moments you're working toward!</div>
        <MiniPath>
          <Node done label="Now" />
          <Node on e={'\u{1F6DF}'} label="Fund full" sub="Jun 2027" />
          <Node e={'✈️'} label="Japan trip" sub="Apr 2028" />
          <Node e={'\u{1F3E1}'} label="Deposit" sub="2031" />
        </MiniPath>
      </>
    ),
    feats: [
      { e: '\u{1F5FA}\u{FE0F}', bg: W2.lil, title: 'Money timeline', text: "The moments you're saving for" /* w2_15 */ },
      { e: '\u{1F4B8}', bg: W2.mint, title: 'Every payday', text: 'Where your money goes on its own' },
      { e: '\u{1F4A1}', bg: W2.blue, title: 'Insights', text: 'Tips made from your own spending' },
      { e: '\u{1F4DD}', bg: W2.pink, title: 'Notes and reminders', text: 'Plans and to-dos for your money' },
    ],
    tip: 'add one step to your timeline, like an emergency fund being full!',
  },
  {
    key: 'budget', e: '\u{1F4B0}', name: 'Budget', line: 'Plan your month and track it', bg: W2.pink, ink: W2.pinkT, where: 'Second tab',
    sub: 'Plan your month, then track it as you go.',
    preview: () => (
      <>
        <div style={cTitle}>{monthName()} so far {'✨'}</div>
        <MiniPath dashed>
          <Node bg={W2.mint} e={'\u{1F4B0}'} label="Came in" pill={{ text: '$4,200', bg: W2.mint, ink: W2.mintT }} />
          <Node bg={W2.blue} e={'\u{1F6CD}\u{FE0F}'} label="Spent" pill={{ text: '$2,940', bg: W2.blue, ink: W2.blueT }} />
          <Node bg={W2.lil} e={'\u{1F437}'} label="Saved" pill={{ text: '$600', bg: W2.lil, ink: W2.lilT }} />
          <Node bg={W2.pink} e={'\u{1F4C9}'} label="Leftover" pill={{ text: '$660', bg: W2.pink, ink: W2.pinkT }} />
        </MiniPath>
        <div style={{ background: W2.field, borderRadius: 16, padding: '10px 12px', marginTop: 12 }}>
          <div className="flex justify-between" style={{ fontSize: 11, color: W2.soft }}>
            <span>Budget used <b style={{ color: W2.ink, fontWeight: 600 }}>62%</b></span>
            <span>Month gone <b style={{ color: W2.ink, fontWeight: 600 }}>90%</b></span>
          </div>
          <div className="grid grid-cols-2" style={{ gap: 8, marginTop: 6 }}>
            <Bar pct={62} colour={W2.pinkBar} />
            <Bar pct={90} colour={W2.lilBar} />
          </div>
        </div>
      </>
    ),
    feats: [
      { e: '➕', bg: W2.pink, title: 'Add as you spend', text: 'Expenses, income and savings' },
      { e: '\u{1F3AF}', bg: W2.lil, title: 'Set up each month', text: 'Income and a budget per category' },
      { e: '\u{1F399}\u{FE0F}', bg: W2.mint, title: 'Log with Siri', text: '"Log an expense in Worthly"' },
      { e: '\u{1F4CA}', bg: W2.blue, title: 'Your year', text: 'Stats and trends month by month' },
    ],
    tip: () => `tap "Set up ${monthName()}" to add your income and budgets!`,
  },
  {
    key: 'wealth', e: '\u{1F331}', name: 'Wealth', line: 'Net worth, funds and investments', bg: W2.mint, ink: W2.mintT, where: 'Third tab',
    sub: 'Watch your net worth grow, one milestone at a time!',
    preview: () => (
      <>
        <div className="flex items-start justify-between">
          <div>
            <div style={{ ...cSub, marginTop: 0 }}>Net worth</div>
            <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.03em', color: W2.ink, marginTop: 2 }}>$18,240</div>
          </div>
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '4px 9px', borderRadius: 999, background: W2.mint, color: W2.mintT, marginTop: 4 }}>{'↗'} $640 this month</span>
        </div>
        <MiniPath>
          <Node done label="$10k" sub="Passed" />
          <Node on e={'\u{1FAB4}'} label="$25k" sub="Jul 2027" />
          <Node e={'\u{1F337}'} label="$50k" sub="2030" />
          <Node e={'\u{1F333}'} label="$100k" sub="2035" />
        </MiniPath>
      </>
    ),
    feats: [
      { e: '\u{1F49A}', bg: W2.mint, title: 'Net worth', text: 'Milestones that tick off as you pass them' },
      { e: '\u{1F437}', bg: W2.blue, title: 'Sinking funds', text: 'Goals and where each is heading' },
      { e: '\u{1F4C8}', bg: W2.lil, title: 'Investments', text: 'RRSP, TFSA and more, month by month' },
      { e: '\u{1F52E}', bg: W2.pink, title: 'Future income', text: 'Plan bonuses and extra money' },
    ],
    tip: "give one sinking fund a goal, so you can see when it'll be full!",
  },
  {
    key: 'calendar', e: '\u{1F5D3}\u{FE0F}', name: 'Calendar', line: 'No-spend days and willpower wins', bg: W2.blue, ink: W2.blueT, where: 'Fourth tab',
    sub: 'Build no-spend habits, one day at a time.',
    preview: () => (
      <>
        <div className="flex items-baseline justify-between">
          <span style={cTitle}>No-spend days {'\u{1F3AF}'}</span>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: W2.mintT }}>8 of 12</span>
        </div>
        <Bar pct={67} colour={W2.mintBar} style={{ marginTop: 8 }} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 5, margin: '10px auto 0', maxWidth: 260 }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <span key={'d' + i} style={{ fontSize: 9, color: W2.faint, textAlign: 'center' }}>{d}</span>
          ))}
          {['g', 'p', 'g', 'g', 'y', 'g', 'p', 'g', 'g', 'p', 'g', 'today', 'e', 'e'].map((d, i) => (
            <i
              key={i}
              style={{
                display: 'block', aspectRatio: '1', borderRadius: 999,
                background: d === 'g' ? W2.mintBar : d === 'p' ? W2.pinkBar : d === 'y' ? W2.butterBar : d === 'e' ? '#F4F1FB' : 'transparent',
                boxShadow: d === 'today' ? `inset 0 0 0 2px ${W2.lilBar}` : 'none',
              }}
            />
          ))}
        </div>
      </>
    ),
    feats: [
      { e: '✅', bg: W2.mint, title: 'Mark each day', text: 'No-spend, neutral or spent' },
      { e: '\u{1F3AF}', bg: W2.blue, title: 'Monthly target', text: 'Plan your no-spend days for the month' },
      { e: '\u{1F4AA}\u{1F3FE}', bg: W2.pink, title: 'Willpower wins', text: 'Things you chose not to buy' },
      { e: '\u{1F4B0}', bg: W2.lil, title: 'Money kept', text: 'What you saved by not spending' },
    ],
    tip: 'mark today, then set a target for the month!',
  },
  {
    key: 'wishlist', e: '✨', name: 'Wishlist', line: 'Save up for what you really want', bg: W2.butter, ink: W2.butterT, where: 'Fifth tab',
    sub: 'Save up for what you really want.',
    preview: () => (
      <>
        <div style={cTitle}>Things you want {'✨'}</div>
        {[['\u{1F45C}', W2.pink, W2.pinkBar, 'New handbag', '$240', 70], ['\u{1F3A7}', W2.blue, W2.blueBar, 'Headphones', '$180', 35]].map(([e, bg, bar, n, a, pct], i) => (
          <div key={n} className="flex items-center" style={{ gap: 10, marginTop: i ? 10 : 12 }}>
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: bg, fontSize: 16 }}>{e}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="flex justify-between" style={{ fontSize: 12.5, fontWeight: 600, color: W2.ink }}><span>{n}</span><span>{a}</span></span>
              <Bar pct={pct} colour={bar} style={{ marginTop: 6 }} />
            </span>
          </div>
        ))}
      </>
    ),
    feats: [
      { e: '\u{1F517}', bg: W2.butter, title: 'Add anything', text: 'By link, photo or emoji' },
      { e: '\u{1F9FE}', bg: W2.mint, title: 'Tax included', text: 'See the real price before you buy' },
      { e: '\u{1F914}', bg: W2.lil, title: 'Should I buy this?', text: 'A quick quiz before you spend' },
      { e: '⏳', bg: W2.pink, title: 'Think it over', text: "What you'd give up to buy it" },
    ],
    tip: "add the next thing you're thinking of buying!",
  },
];

const WelcomeTutorial = ({ onComplete, saveUserData, startSlide = 0, userName = '', firstTime = false, aboveTabs = false }) => {
  const single = startSlide > 0 && startSlide <= TABS.length; // opened from a tab's help button
  const [slide, setSlide] = useState(single ? startSlide : 0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const lastSlide = TABS.length + 1; // the safety page
  const footPad = aboveTabs ? 'calc(env(safe-area-inset-bottom, 0px) + 74px)' : undefined;

  const finish = () => {
    saveUserData({ tutorial_completed: true });
    onComplete();
  };
  const next = () => (slide < lastSlide ? setSlide(slide + 1) : finish());
  const back = () => slide > 0 && setSlide(slide - 1);

  // Swipe left and right between pages
  const onTouchEnd = () => {
    if (single || touchStart === null || touchEnd === null) return;
    const d = touchStart - touchEnd;
    if (d > 50) next();
    else if (d < -50) back();
  };
  const swipe = {
    onTouchStart: (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientX); },
    onTouchMove: (e) => setTouchEnd(e.targetTouches[0].clientX),
    onTouchEnd,
  };

  const topRow = (showSkip) => (
    <div className="flex items-center justify-between">
      <W2Dots count={lastSlide} at={slide - 1} />
      {showSkip ? (
        <button type="button" onClick={finish} style={{ border: 0, background: 'none', padding: 0, fontSize: 13, fontWeight: 500, color: W2.soft, cursor: 'pointer' }}>Skip</button>
      ) : <span />}
    </div>
  );

  // ---------------------------------------------------------------- start
  if (slide === 0) {
    const first = String(userName || '').trim().split(' ')[0];
    const stops = [
      ...TABS.map((t) => ({ key: t.key, e: t.e, bg: t.bg, name: t.name, line: t.line })),
      { key: 'safety', e: '\u{1F6E1}\u{FE0F}', bg: '#FFFFFF', ring: true, name: 'Keeping your money safe', line: 'What to never enter' },
    ];
    return (
      <W2Page
        testId="tutorial-start"
        footPad={footPad}
        foot={
          <>
            <button type="button" onClick={next} style={w2Btn} data-testid="tutorial-show-me">Show me</button>
            <button type="button" onClick={finish} style={{ display: 'block', width: '100%', border: 0, background: 'none', padding: '10px 0 0', fontSize: 13, color: W2.soft, cursor: 'pointer' }}>Skip for now</button>
          </>
        }
      >
        <div {...swipe}>
          <h1 style={{ ...w2Title, fontSize: 30, marginTop: 18 }}>You're all set{first ? <>,<br />{first}!</> : '!'} {'\u{1F389}'}</h1>
          <p style={{ ...w2Sub, fontSize: 14.5 }}>Here's a quick look at what Worthly can do!</p>
          <div style={{ position: 'relative', marginTop: 20 }}>
            <div style={{ position: 'absolute', left: 22, top: 24, bottom: 24, borderLeft: '2.5px dashed #D9CFF4' }} />
            {stops.map((s) => (
              <div key={s.key} className="flex items-center" style={{ position: 'relative', gap: 14, background: '#FFFFFF', borderRadius: 20, padding: '8px 14px 8px 10px', marginTop: 7 }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 999, background: s.bg, fontSize: 19, border: `3px solid ${W2.page}`, boxShadow: s.ring ? `inset 0 0 0 2px ${W2.lil}` : 'none', marginLeft: -1 }}>{s.e}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: W2.ink }}>{s.name}</span>
                  <span style={{ display: 'block', fontSize: 12, color: W2.soft, marginTop: 1 }}>{s.line}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </W2Page>
    );
  }

  // ---------------------------------------------------------------- safety
  if (slide === lastSlide) {
    return (
      <W2Page
        testId="tutorial-safety"
        footPad={footPad}
        foot={
          <div className="flex" style={{ gap: 10 }}>
            <button type="button" onClick={back} style={w2BackBtn}>Back</button>
            <button type="button" onClick={finish} style={{ ...w2Btn, flex: 1, width: 'auto' }} data-testid="tutorial-finish">{firstTime ? 'Start using Worthly \u{1F389}' : 'Done'}</button>
          </div>
        }
      >
        <div {...swipe}>
          {topRow(false)}
          <div className="flex items-start justify-between" style={{ gap: 12, marginTop: 20 }}>
            <h1 style={{ ...w2Title, fontSize: 26 }}>Keeping your<br />money safe</h1>
            <W2Bubble bg={W2.mint} e={'\u{1F6E1}\u{FE0F}'} />
          </div>
          <p style={w2Sub}>Worthly is a tracking app, not a bank. You enter your numbers yourself.</p>
          <div style={{ marginTop: 8 }}>
            <WideFeat e={'\u{1F6AB}'} bg={W2.pink} title="Never enter these" text="Full card or account numbers, your SIN, passwords, PINs or banking logins" />
            <WideFeat e={'✅'} bg={W2.mint} title="This is all Worthly needs" text={'Nicknames like "Scotia Debit", balances and your transactions'} />
            <WideFeat e={'\u{1F512}'} bg={W2.lil} title="We never connect to your bank" text="So we can never see or move your money" />
          </div>
          <Tip label="Good rule of thumb:">if your bank would never ask for it by email or phone, don't enter it here either!</Tip>
        </div>
      </W2Page>
    );
  }

  // ---------------------------------------------------------------- a tab
  const tab = TABS[slide - 1];
  const tip = typeof tab.tip === 'function' ? tab.tip() : tab.tip;
  return (
    <W2Page
      testId={`tutorial-${tab.key}`}
      footPad={footPad}
      foot={single ? (
        <button type="button" onClick={onComplete} style={w2Btn} data-testid="tutorial-got-it">Got it</button>
      ) : (
        <div className="flex" style={{ gap: 10 }}>
          <button type="button" onClick={back} style={w2BackBtn}>Back</button>
          <button type="button" onClick={next} style={{ ...w2Btn, flex: 1, width: 'auto' }} data-testid="tutorial-next">Next</button>
        </div>
      )}
    >
      <div {...swipe}>
        {single ? null : topRow(true)}
        <Preview>{tab.preview()}</Preview>
        <div className="flex items-end justify-between" style={{ gap: 10, marginTop: 18 }}>
          <h1 style={{ ...w2Title, fontSize: 26 }}>{tab.name}</h1>
          <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 600, padding: '5px 10px', borderRadius: 999, background: tab.bg, color: tab.ink }}>{tab.e} {tab.where}</span>
        </div>
        <p style={w2Sub}>{tab.sub}</p>
        <div className="grid grid-cols-2" style={{ gap: 8, marginTop: 14 }}>
          {tab.feats.map((f) => <Feat key={f.title} {...f} />)}
        </div>
        <Tip>{tip}</Tip>
      </div>
    </W2Page>
  );
};

export default WelcomeTutorial;
